import { useMemo, useRef, useState, useEffect } from "react";
import { useParams } from "@tanstack/react-router";
import { useThrottledState } from "@mantine/hooks";
import {
    ActionIcon,
    Badge,
    Button,
    Center,
    Group,
    Loader,
    LoadingOverlay,
    ScrollArea,
    Stack,
    Text,
    Tooltip,
    Transition,
} from "@mantine/core";
import { IconArrowBarToDown, IconFileExport } from "@tabler/icons-react";

import {
    ChatBotHistory,
    useGetApiBotProviderGet,
    useGetApiBotProviderUserSettingsGet,
    useGetApiChatBotHistoryGet,
    useGetApiChatBotHistoryGetAfter,
    useGetApiChatBotHistoryGetBefore,
    useGetApiWorkspaceGet,
} from "@/API";
import { DateToTimestamp, TimestampToDate } from "@/Helpers/DateHelper";
import { AppBreadcrumbs } from "@/Components/AppBreadcrumbs";
import { SignalrStateBadge } from "@/Components/SignalrStateBadge";

import { BotProviderUserSettingsButton } from "./BotProviderUserSettingsButton";
import { ChatBotHistoryItem } from "./ChatBotHistoryItem";
import { ChatBotHistoryToolbar } from "./ChatBotHistoryToolbar";
import { ChatBotSendMessageForm } from "./ChatBotSendMessageForm";
import { ChatBotHistoryExportModal } from "./ChatBotHistoryExportModal";
import { useBotProviderUserHub } from "./useBotProviderUserHub";

const PAGE_SIZE = 10;

export function UserChatBotHistory() {
    const { workspaceId, botProviderId, userId } = useParams({
        from: "/workspace/$workspaceId/botProvider/$botProviderId/user/$userId",
    });

    const workspace = useGetApiWorkspaceGet({ id: workspaceId });
    const botProvider = useGetApiBotProviderGet({ workspaceId, id: botProviderId });

    const [withToolRecords, setWithToolRecords] = useState(false);
    const [enableMarkdown, setEnableMarkdown] = useState(true);
    const [recordsLimit, setRecordsLimit] = useState(PAGE_SIZE);
    const [exportModalOpened, setExportModalOpened] = useState(false);

    // 時間查找模式狀態
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [searchTimestamp, setSearchTimestamp] = useState<number | null>(null);
    const [beforeCount, setBeforeCount] = useState(PAGE_SIZE);
    const [afterCount, setAfterCount] = useState(PAGE_SIZE);

    const isLatestMode = searchTimestamp === null;
    const isLatestModeRef = useRef(isLatestMode);
    isLatestModeRef.current = isLatestMode;

    const userSettings = useGetApiBotProviderUserSettingsGet({
        workspaceId,
        botProviderId,
        userId,
    });
    const historyStartTimestamp = userSettings.data?.data?.historyStartTimestamp ?? 0;
    const isDisableReply = userSettings.data?.data?.disableReply ?? false;

    // 最新紀錄模式查詢
    const historyListLatest = useGetApiChatBotHistoryGet(
        { botProviderId, userId, withToolRecords, recordsLimit },
        { query: { enabled: isLatestMode } as any }
    );

    // 時間查找模式查詢 (載入指定時間前與後的紀錄)
    const historyListBefore = useGetApiChatBotHistoryGetBefore(
        {
            botProviderId,
            userId,
            timestamp: (searchTimestamp ?? 0) + 1,
            count: beforeCount,
            withToolRecords,
        },
        { query: { enabled: !isLatestMode && searchTimestamp !== null } as any }
    );

    const historyListAfter = useGetApiChatBotHistoryGetAfter(
        {
            botProviderId,
            userId,
            timestamp: searchTimestamp ?? 0,
            count: afterCount,
            withToolRecords,
        },
        { query: { enabled: !isLatestMode && searchTimestamp !== null } as any }
    );

    // 整合要顯示的歷史紀錄清單
    const displayHistoryList = useMemo<ChatBotHistory[]>(() => {
        if (isLatestMode) {
            return historyListLatest.data?.data ?? [];
        } else {
            const beforeData = historyListBefore.data?.data ?? [];
            const afterData = historyListAfter.data?.data ?? [];
            const map = new Map<string, ChatBotHistory>();
            beforeData.forEach((item) => {
                if (item.id) map.set(item.id, item);
            });
            afterData.forEach((item) => {
                if (item.id) map.set(item.id, item);
            });
            return Array.from(map.values()).sort(
                (a, b) => (a.createdTimestamp ?? 0) - (b.createdTimestamp ?? 0)
            );
        }
    }, [
        isLatestMode,
        historyListLatest.data?.data,
        historyListBefore.data?.data,
        historyListAfter.data?.data,
    ]);

    const isFetching = isLatestMode
        ? historyListLatest.isFetching
        : historyListBefore.isFetching || historyListAfter.isFetching;

    const viewport = useRef<HTMLDivElement>(null);
    const prevScrollHeightRef = useRef<number | null>(null);
    const [isBottom, setIsBottom] = useThrottledState(false, 250);

    const scrollToBottom = () =>
        viewport.current?.scrollTo({ top: viewport.current.scrollHeight, behavior: "smooth" });

    const handleLoadMoreLatest = () => {
        if (viewport.current) {
            prevScrollHeightRef.current = viewport.current.scrollHeight;
        }
        setRecordsLimit((prev) => prev + PAGE_SIZE);
    };

    const handleLoadMoreBefore = () => {
        if (viewport.current) {
            prevScrollHeightRef.current = viewport.current.scrollHeight;
        }
        setBeforeCount((prev) => prev + PAGE_SIZE);
    };

    const handleLoadMoreAfter = () => {
        setAfterCount((prev) => prev + PAGE_SIZE);
    };

    const handleSearchTime = () => {
        if (!selectedDate) return;
        const ts = DateToTimestamp(selectedDate);
        setSearchTimestamp(ts);
        setBeforeCount(PAGE_SIZE);
        setAfterCount(PAGE_SIZE);
    };

    const handleBackToLatest = () => {
        setSelectedDate(null);
        setSearchTimestamp(null);
        setBeforeCount(PAGE_SIZE);
        setAfterCount(PAGE_SIZE);
        setRecordsLimit(PAGE_SIZE);
    };

    const { connectionState } = useBotProviderUserHub({
        workspaceId,
        botProviderId,
        userId,
        onHistoryUpdate: () => {
            if (isLatestModeRef.current) {
                historyListLatest.refetch();
            } else {
                historyListBefore.refetch();
                historyListAfter.refetch();
            }
        },
    });

    useEffect(() => {
        if (prevScrollHeightRef.current !== null && viewport.current) {
            const heightDiff = viewport.current.scrollHeight - prevScrollHeightRef.current;
            viewport.current.scrollTop += heightDiff;
            prevScrollHeightRef.current = null;
        } else if (isBottom) {
            scrollToBottom();
        }
    }, [displayHistoryList]);

    return (
        <Stack
            pos="relative"
            h="calc(100vh - var(--app-shell-header-height, 0px) - var(--app-shell-footer-height, 0px) - var(--app-shell-padding) * 2)"
            flex={1}
            gap={0}
        >
            <LoadingOverlay
                visible={botProvider.isFetching}
                zIndex={1000}
                overlayProps={{ radius: "sm", blur: 2 }}
            />

            <AppBreadcrumbs
                items={[
                    {
                        label: "工作空間列表",
                        linkProps: { to: "/workspace" },
                    },
                    {
                        label: workspace.data?.data.name ?? "",
                        linkProps: { to: "/workspace/$workspaceId", params: { workspaceId } },
                    },
                    {
                        label: "機器人串接",
                        linkProps: {
                            to: "/workspace/$workspaceId/botProvider",
                            params: { workspaceId },
                        },
                    },
                    {
                        label: (botProvider.data?.data.name ?? "") + "聊天紀錄",
                        linkProps: {
                            to: "/workspace/$workspaceId/botProvider/$botProviderId/history",
                            params: { workspaceId, botProviderId },
                        },
                    },
                    {
                        label: "使用者聊天紀錄",
                        active: true,
                    },
                ]}
            />

            <Group>
                <SignalrStateBadge state={connectionState} />
                <BotProviderUserSettingsButton
                    workspaceId={workspaceId}
                    botProviderId={botProviderId}
                    userId={userId}
                    onUpdate={() => {
                        if (isLatestMode) historyListLatest.refetch();
                        else {
                            historyListBefore.refetch();
                            historyListAfter.refetch();
                        }
                        userSettings.refetch();
                    }}
                >
                    <Button>設定</Button>
                </BotProviderUserSettingsButton>

                <Button
                    leftSection={<IconFileExport size={16} />}
                    variant="default"
                    onClick={() => setExportModalOpened(true)}
                >
                    匯出紀錄
                </Button>

                {isDisableReply && (
                    <Badge color="red" variant="filled" size="lg">
                        已關閉自動回應
                    </Badge>
                )}

                {historyStartTimestamp > 0 && (
                    <Badge color="orange" variant="light" size="lg">
                        歷史起點: {TimestampToDate(historyStartTimestamp).toLocaleString()}
                    </Badge>
                )}

                {isFetching && (
                    <Tooltip label="更新資料中...">
                        <Center>
                            <Loader />
                        </Center>
                    </Tooltip>
                )}
            </Group>

            <ChatBotHistoryToolbar
                withToolRecords={withToolRecords}
                onChangeWithToolRecords={setWithToolRecords}
                enableMarkdown={enableMarkdown}
                onChangeEnableMarkdown={setEnableMarkdown}
                selectedDate={selectedDate}
                onChangeSelectedDate={setSelectedDate}
                isLatestMode={isLatestMode}
                onSearchTime={handleSearchTime}
                onBackToLatest={handleBackToLatest}
            />

            <ScrollArea
                viewportRef={viewport}
                offsetScrollbars
                flex={1}
                my={10}
                onScrollCapture={() => {
                    const isAtBottom =
                        (viewport.current?.scrollHeight ?? 0) -
                            (viewport.current?.clientHeight ?? 0) -
                            (viewport.current?.scrollTop ?? 0) <
                        100;
                    setIsBottom(isAtBottom);
                }}
            >
                {isLatestMode ? (
                    (() => {
                        const fetchedCount = historyListLatest.data?.data?.length ?? 0;
                        const hasMore = fetchedCount >= recordsLimit;
                        return (
                            <Center my="xs">
                                {hasMore ? (
                                    <Button
                                        variant="subtle"
                                        size="xs"
                                        loading={historyListLatest.isFetching}
                                        onClick={handleLoadMoreLatest}
                                    >
                                        載入更多歷史紀錄
                                    </Button>
                                ) : fetchedCount > 0 ? (
                                    <Text size="xs" c="dimmed">
                                        已載入全部歷史紀錄
                                    </Text>
                                ) : null}
                            </Center>
                        );
                    })()
                ) : (
                    (() => {
                        const fetchedBeforeCount = historyListBefore.data?.data?.length ?? 0;
                        const hasMoreBefore = fetchedBeforeCount >= beforeCount;
                        return (
                            <Center my="xs">
                                {hasMoreBefore ? (
                                    <Button
                                        variant="subtle"
                                        size="xs"
                                        loading={historyListBefore.isFetching}
                                        onClick={handleLoadMoreBefore}
                                    >
                                        載入更早歷史紀錄
                                    </Button>
                                ) : (
                                    <Text size="xs" c="dimmed">
                                        已載入最頂端歷史紀錄
                                    </Text>
                                )}
                            </Center>
                        );
                    })()
                )}

                {displayHistoryList.length === 0 && (
                    <Center my="xl">
                        <Text size="sm" c="dimmed">
                            {isLatestMode ? "尚無聊天紀錄" : "指定時間範圍內無聊天紀錄"}
                        </Text>
                    </Center>
                )}

                {displayHistoryList.map((history) => (
                    <ChatBotHistoryItem
                        key={history.id}
                        history={history}
                        historyStartTimestamp={historyStartTimestamp}
                        enableMarkdown={enableMarkdown}
                    />
                ))}

                {!isLatestMode && displayHistoryList.length > 0 && (
                    <Stack align="center" my="md" gap="xs">
                        {(() => {
                            const fetchedAfterCount = historyListAfter.data?.data?.length ?? 0;
                            const hasMoreAfter = fetchedAfterCount >= afterCount;
                            return hasMoreAfter ? (
                                <Button
                                    variant="subtle"
                                    size="xs"
                                    loading={historyListAfter.isFetching}
                                    onClick={handleLoadMoreAfter}
                                >
                                    載入較新歷史紀錄
                                </Button>
                            ) : (
                                <Text size="xs" c="dimmed">
                                    已載入此範圍後的全部歷史紀錄
                                </Text>
                            );
                        })()}
                        <Button
                            size="xs"
                            variant="light"
                            color="blue"
                            onClick={handleBackToLatest}
                        >
                            回到最新紀錄
                        </Button>
                    </Stack>
                )}
            </ScrollArea>

            <Transition
                mounted={!isBottom}
                transition="fade-up"
                duration={200}
                timingFunction="ease"
            >
                {(styles) => (
                    <ActionIcon
                        style={{
                            zIndex: 10,
                            position: "absolute",
                            right: "20px",
                            bottom: "50px",
                            ...styles,
                        }}
                        variant="light"
                        radius="xl"
                        size="xl"
                        onClick={scrollToBottom}
                    >
                        <IconArrowBarToDown />
                    </ActionIcon>
                )}
            </Transition>

            <ChatBotSendMessageForm
                workspaceId={workspaceId}
                botProviderId={botProviderId}
                userId={userId}
                botProviderType={botProvider.data?.data.type}
                isLatestMode={isLatestMode}
                onBackToLatest={handleBackToLatest}
            />

            <ChatBotHistoryExportModal
                opened={exportModalOpened}
                onClose={() => setExportModalOpened(false)}
                defaultSearch={{
                    botProviderId,
                    userId,
                    startTimestamp: searchTimestamp ?? undefined,
                }}
                title={`匯出使用者 ${userSettings.data?.data?.name || userId} 聊天紀錄`}
            />
        </Stack>
    );
}
